/**
 * AppHeader — sticky transparent header.
 *
 * Sits at the top of the viewport and stays there while content
 * scrolls underneath. Once the user has scrolled even a little, the
 * header transitions from transparent to a glass capsule so the
 * content scrolling under it gets a real backdrop-blur — that's the
 * iOS 26 effect the user wants to see.
 *
 * Luna is **dark-only**, so there's no theme toggle. The top-right
 * slot is occupied by `<NotificationBell/>` which shows an unread
 * badge + dropdown list of all alerts the backend has sent the user.
 */
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlassPill } from '@/components/glass';
import { LiquidNumber } from '@/components/glass/LiquidNumber';
import { cn } from '@/lib/cn';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { NotificationBell } from './NotificationBell';
import { BatteryWidget } from './BatteryWidget';

interface AppHeaderProps {
  /** Current USDT balance to display in the header pill */
  balance: number | string;
}

export function AppHeader({ balance }: AppHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { haptic } = useTelegramWebApp();
  const { t } = useTranslation();

  // Add a glass background once the page has scrolled at all
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 6);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={cn(
        'sticky top-0 z-20 flex items-center justify-between',
        'px-3 pt-[max(env(safe-area-inset-top),22px)] pb-3',
        'transition-all duration-500 ease-[var(--ease-liquid)]',
      )}
      style={{
        backdropFilter: scrolled ? 'blur(8px)' : 'none',
        WebkitBackdropFilter: scrolled ? 'blur(8px)' : 'none',
        background: scrolled
          ? 'linear-gradient(180deg, color-mix(in oklab, var(--bg-base) 75%, transparent) 0%, color-mix(in oklab, var(--bg-base) 30%, transparent) 100%)'
          : 'transparent',
        borderBottom: scrolled
          ? '1px solid var(--glass-stroke)'
          : '1px solid transparent',
      }}
    >
      {/* Brand monogram */}
      <motion.div
        className="flex items-center gap-2"
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.05 }}
      >
        <BrandMark />
        <div className="flex flex-col leading-tight">
          <span className="font-display text-[14px] font-extrabold tracking-[0.02em] text-[var(--text-primary)]">
            LUN∆
          </span>
          <span className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)]">
            Pro₮okol
          </span>
        </div>
      </motion.div>

      <motion.div
        className="flex items-center gap-1.5"
        initial={{ opacity: 0, x: 8 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <button
          type="button"
          onClick={() => { haptic.light(); navigate('/wallet'); }}
          aria-label={t('header.openWalletAria')}
          data-tour="balance"
          className="inline-flex cursor-pointer active:scale-[0.96] transition-transform"
        >
          <GlassPill tone="lunar" dot className="!px-2.5 !h-9 !text-[11px]">
            <LiquidNumber value={balance} suffix="₮" suffixClassName="ml-1" className="text-[12px] font-extrabold" />
          </GlassPill>
        </button>
        <BatteryWidget />
        <span data-tour="bell" className="inline-flex">
          <NotificationBell />
        </span>
      </motion.div>
    </header>
  );
}

/**
 * Stylized lunar crescent monogram.
 */
function BrandMark() {
  return (
    <div className="relative h-9 w-9 flex items-center justify-center lg-pill !rounded-full">
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          <radialGradient id="bm-glow" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="var(--accent-primary)" stopOpacity="0.9" />
            <stop offset="100%" stopColor="var(--accent-primary)" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="bm-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="var(--accent-primary)" />
            <stop offset="100%" stopColor="var(--accent-secondary)" />
          </linearGradient>
        </defs>
        <circle cx="12" cy="12" r="9" fill="url(#bm-glow)" />
        <path
          d="M19 12a7 7 0 1 1-7-7 5.5 5.5 0 0 0 7 7Z"
          stroke="url(#bm-stroke)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="15" cy="9" r="0.9" fill="var(--accent-primary)" />
      </svg>
    </div>
  );
}
